/**
 * BilingualForm — a single contact/appointment form that flips labels and
 * validation messages based on the current `lang` prop ("en" | "es").
 *
 * Posts to `/api/leads` with { ..., lang } so backend can route ES leads
 * into the Spanish drip campaign.
 *
 * Drop into any page: <BilingualForm lang="es" source="contact_page" />
 */
import React, { useState } from 'react';
import axios from 'axios';
import { t } from '@/i18n/strings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_BASE =
  'w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition';

const BilingualForm = ({
  lang = 'en',
  source = 'contact_form',
  showInsurance = true,
  showMessage = true,
  className = '',
  onSuccess,
}) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    insurance: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const change = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: undefined });
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('validation_required', lang);
    if (!form.email.trim()) next.email = t('validation_required', lang);
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = t('validation_email', lang);
    if (!form.phone.trim()) next.phone = t('validation_required', lang);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/leads`, {
        ...form,
        lang,
        source,
        page: typeof window !== 'undefined' ? window.location.pathname : null,
      });
      if (res.data?.success) {
        setDone(true);
        onSuccess?.(res.data);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || t('toast_error', lang);
      setErrors({ form: typeof msg === 'string' ? msg : t('toast_error', lang) });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="bilingual-form-success"
        className={`bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center ${className}`}
      >
        <p className="text-emerald-700 font-semibold text-lg">{t('toast_success', lang)}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`space-y-4 ${className}`} data-testid="bilingual-form" noValidate>
      <div>
        <label htmlFor="bf-name" className="block text-sm font-medium text-gray-700 mb-1">
          {t('form_name', lang)}
        </label>
        <input
          id="bf-name"
          name="name"
          type="text"
          value={form.name}
          onChange={change}
          className={FIELD_BASE}
          data-testid="bilingual-form-name"
          autoComplete="name"
        />
        {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="bf-email" className="block text-sm font-medium text-gray-700 mb-1">
          {t('form_email', lang)}
        </label>
        <input
          id="bf-email"
          name="email"
          type="email"
          value={form.email}
          onChange={change}
          className={FIELD_BASE}
          data-testid="bilingual-form-email"
          autoComplete="email"
          inputMode="email"
        />
        {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
      </div>

      <div>
        <label htmlFor="bf-phone" className="block text-sm font-medium text-gray-700 mb-1">
          {t('form_phone', lang)}
        </label>
        <input
          id="bf-phone"
          name="phone"
          type="tel"
          value={form.phone}
          onChange={change}
          className={FIELD_BASE}
          data-testid="bilingual-form-phone"
          autoComplete="tel"
          inputMode="tel"
        />
        {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
      </div>

      {showInsurance && (
        <div>
          <label htmlFor="bf-insurance" className="block text-sm font-medium text-gray-700 mb-1">
            {t('form_insurance', lang)}
          </label>
          <input
            id="bf-insurance"
            name="insurance"
            type="text"
            value={form.insurance}
            onChange={change}
            className={FIELD_BASE}
            data-testid="bilingual-form-insurance"
          />
        </div>
      )}

      {showMessage && (
        <div>
          <label htmlFor="bf-message" className="block text-sm font-medium text-gray-700 mb-1">
            {t('form_message', lang)}
          </label>
          <textarea
            id="bf-message"
            name="message"
            rows={3}
            value={form.message}
            onChange={change}
            className={FIELD_BASE}
            data-testid="bilingual-form-message"
          />
        </div>
      )}

      {errors.form && (
        <p data-testid="bilingual-form-error" className="text-red-600 text-sm">{errors.form}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="bilingual-form-submit"
        className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-md transition-colors"
      >
        {submitting ? t('form_submitting', lang) : t('form_submit', lang)}
      </button>
    </form>
  );
};

export default BilingualForm;
